<template lang="html">
  <div :style="containers_style" class="container">
    <!-- The timeline slides -->
    <div :key="version" class="player" @click="playToggle">
      <!-- Image slides -->
      <div
        v-for="(image, idx) in slideArray"
        :key="idx + image.file"
        :style="slide_area_style"
        class="images slides"
      >
        <ImageDisplay
          v-if="imageDirectoryLargest != null"
          :filepath="returnImagePath(image.file)"
          :index="idx"
          :preload="preloadImage(idx)"
          @image-loaded="imageLoaded"
        />
      </div>
      <!-- Lowerthird slides -->
      <div
        v-for="(item, idx) in lowerthirdsArray"
        :key="idx + item.inpoint"
        :style="slide_area_style"
        class="lowerthirds slides"
      >
        <Lowerthird
          :name="item.name"
          :title="item.title"
          :controls-auto-hide="controlsAutoHide"
        />
      </div>
    </div>
    <!-- UI's and globals -->
    <BigButton
      v-show="showBigButton && audio_source === null && firstImageLoaded"
      :class="{scaling: fillscreen || forceFillscreen || fullscreenFillscreen}"
      class="bigbutton"
    />
    <Title v-if="title != null && displayTitle" :text="title" class="title" />
    <ImageCaption v-show="showImageCaptions" :text="active_caption" />
    <!-- Controls -->
    <div v-if="audio_source === null && controlsReady">
      <!-- audio element via plyr -->
      <div v-if="controlsAutoHide" class="controls_background" />
      <Controls
        :ref="'audio-' + _uid"
        :audio_url="audio_src"
        :has_captions="hasCaptions"
        :captions_active="showImageCaptions"
        class="controls"
        @nextImage="nextImage"
        @previousImage="previousImage"
        @fullscreen="toggleFullscreen"
        @toggleCaptions="toggleCaptions"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { createTimeline } from "./timeline"
import {
  returnSlideArrayFromXml,
  returnLowerthirdsArrayFromXml,
  returnShowMetaData,
  returnImageDirectoryFromMeta
} from "./soundslidesData"
import axios from 'axios'
import ImageCaption from "./ImageCaption"
import ImageDisplay from "./ImageDisplay"
import Lowerthird from "./Lowerthird"
import Controls from "./Controls"
import Title from "./Title"
import BigButton from "./BigButton"

import { TweenLite, Power2, CSSPlugin } from "gsap/all";
// Imports MUST come from gsap/all, or it will import everything!
// Also, CSSPlugin is used in everything, so import it even though it isn't directly used.
// See: https://greensock.com/forums/topic/18437-import-from-gsap-vs-from-gsapall/
// Ensure modules don't get dropped by tree-shaking
const activatedGsapModules = [
    TweenLite,
    Power2,
    CSSPlugin
];
console.log(activatedGsapModules);

export default {
  name: "SoundslidesPlayer",
  components:{
    ImageCaption,
    ImageDisplay,
    Lowerthird,
    Title,
    Controls,
    BigButton
  },
  props:{
    width: {
      type: Number,
      default: () => null
    },
    height: {
      type: Number,
      default: () => null
    },
    audio_source: {
      type: HTMLAudioElement,
      default: () => null
    },
    editing_audio_source_url:{
      type: String,
      default: ()=> null
    },
    editing_initial_slide_data: {
      type: Array,
      default: () => null
    },
    path_to_assets: {
      type: String,
      default: () => null
    },
    fillscreen: {
      type: Boolean,
      default: () => false
    },
    useShowDimensions: {
      type: Boolean,
      default: () => false
    },
    path: {
      type: String,
      default: () => "/shows/01/"
    },
    soloPlayer:{
      // soloPlayer is when the player is the only thing on the page.
      type: Boolean,
      default: () => false
    },
    force_preload:{
      type: Boolean,
      default: () => false
    },
    always_show_controls:{
      type: Boolean,
      default: () => false
    }
  },
  data(){
    return {
      $bigbutton: null,
      $controls_background: null,
      $controls: null,
      $title: null,
      audioDuration: 0,
      backgroundColor: '#000',
      controlFadeDelayMs: 4500,
      controlFadeTimeout: null,
      controlsAutoHide: true,
      controlsBgTween: null,
      controlsReady: false,
      controlsTween: null,
      currentAudioTime: 0,
      currentSlide: {},
      currentSlideIndex: 0,
      currentTimelineTime: 0,
      displayTitle: false,
      firstImageLoaded: false,
      forceFillscreen: false,
      fullscreenFillscreen: false,
      hasCaptions: false,
      imageDirectoryLargest: null,
      lowerthirdsArray: [],
      metaDict: {},
      mp3Filename: "audio_hi.mp3",
      parentHeight: 0,
      parentWidth: 0,
      plyr_height: 50,
      preloadAllImages: false,
      preloadAllImagesTimeThresholdSec: 20, // At this time point, preload everything.
      preloadImageNumber: 3, // lookahead and preload,
      showBigButton: true,
      showImageCaptions: false,
      slideArray: [],
      timeDeltaAccuracySecs: 0.125, // Below this threshold, the GSAP timeline is corrected.
      timeDeltaWatcherInt: null,
      timeline: null,
      transitionTime: 1, // The actual image tween-fade.
      version_number: 0,
      windowHeight: 0,
      windowWidth: 0,
      xmlFilename: "soundslide.xml"
    }
  },
  computed:{
    computed_width(){
      if(this.forceFillscreen || this.fullscreenFillscreen){
        return this.windowWidth;
      }
      if(this.defined_width){
        return this.defined_width;
      }
      if(this.fillscreen){
        return this.windowWidth;
      }
      return this.parentWidth;
    },
    computed_height(){
      if(this.forceFillscreen || this.fullscreenFillscreen){
        return this.windowHeight;
      }
      if(this.defined_height){
        return this.defined_height;
      }
      if(this.fillscreen){
        return this.windowHeight;
      }
      // Size of parent div.
      return this.parentHeight;
    },
    defined_width(){
      if(this.width){
        return this.width;
      }
      if(this.useShowDimensions){
        if("custom_width" in this.metaDict){
          return parseInt(this.metaDict.custom_width, 10);
        }
        return 1000; // Default basic width.
      }
      return null;
    },
    defined_height(){
      if(this.height){
        return this.height;
      }
      if(this.useShowDimensions){
        if("custom_height" in this.metaDict){
          return parseInt(this.metaDict.custom_height, 10);
        }
        return 667; // Default basic width.
      }
      return null;
    },
    containers_style(){
      var _style = {
        width: this.computed_width + 'px',
        height: this.computed_height + 'px'
      };
      return _style;
    },
    slide_area_style(){
      var _style = {
        width: this.computed_width + 'px',
        height: this.computed_height + 'px'
      };
      if(!this.controlsAutoHide){
        _style.height = (this.computed_height - this.plyr_height) + 'px'
      }
      return _style;
    },
    audio_src(){
      if(this.editing_audio_source_url){
        return this.editing_audio_source_url;
      }else{
        return this.path + this.mp3Filename;
      }
    },
    has_external_audio(){
      return this.audio_source !== null;
    },
    audio_ref(){
      if(this.audio_source){
        return this.audio_source;
      }
      return this.$refs["audio-" + this._uid].player;
    },
    version(){
      // This value will force Vue to redraw the timeline's div
      // when version_number is changed.
      // This only happens when 'setSlideData' is called from
      // the editor.
      return "version" + this.version_number
    },
    active_caption(){
      return this.currentSlide.caption;
    },
    title(){
      return this.metaDict.header_headline;
    }
  },
  watch:{
    currentSlide(value, oldValue){
      if(value != oldValue){
        // Find index and emit out to parent.
        // Emit ONLY on change.
        var slideIndex = _.findIndex(this.slideArray, value);
        this.$emit('onSlideChange', slideIndex)
        // Console.
        console.log(slideIndex, value.file);
      }
    }
  },
  mounted(){
    // Load in XML and parse.
    if(!this.editing_initial_slide_data){
      // Load external data file.
      this.loadShowData();
    }else{
      // Direct load from editing_* files.
      this.slideArray = this.editing_initial_slide_data;
      this.lowerthirdsArray = [];
      this.metaDict = {};
      this.imageDirectoryLargest = "";
      this.loadPlayer();
    }
    if(this.force_preload){
      this.preloadAllImages = true;
    }
    if(this.always_show_controls){
      this.controlsAutoHide = false;
    }
  },
  beforeDestroy(){
    console.log("beforeDestroy")
    // Remove the events and destroy Plyr listeners.
    clearInterval(this.timeDeltaWatcherInt);
    if(this.audio_source === null){
      _.each(["timeupdate", "pause", "play", "seeking", "seeked"], ev => {
        this.audio_ref.off(ev);
      });
      this.audio_ref.destroy()
    }

  },
  methods:{
    loadShowData(){
      var _v = this;
      axios.get(this.path + this.xmlFilename)
        .then(function(response){
          _v.initializeDataFromResponse(response);
        })
        .catch(function (error) {
          // handle error
          console.log(error);
      });
    },
    initializeDataFromResponse(response){
      this.slideArray = returnSlideArrayFromXml(response.data);
      this.lowerthirdsArray = returnLowerthirdsArrayFromXml(response.data);
      this.metaDict = returnShowMetaData(response.data);
      this.imageDirectoryLargest = returnImageDirectoryFromMeta(this.metaDict);
      this.loadPlayer();
    },
    loadPlayer(){
      var _v = this;
      _v.hasCaptions = (_v.metaDict.showCaptions === 'true');
      _v.controlsReady = true;
      _v.displayTitle = ("display_header" in _v.metaDict && _v.metaDict.display_header == "true");
      _v.currentSlide = _v.slideArray[0];
      setTimeout(()=>{
        _v.renderTimeline();
        _v.uiSetup();
        _v.setPageAttributes();
        _v.$nextTick(() => {
          window.addEventListener('resize', () => {
            console.log("resize")
            _v.setDimensionValues();
          });
          // Set initial.
          _v.setDimensionValues();
          _v.setupAudioListeners();
        })
      }, 100);
    },
    uiSetup(){
      // Background.
      // Controls, fade, etc.
      this.$controls = this.$el.querySelector('.controls')
      this.$controls_background = this.$el.querySelector('.controls_background')
      this.$bigbutton = this.$el.querySelector('.bigbutton')
      this.$title = this.$el.querySelector('.title')
      this.$container = this.$el
      // If controlsAutoHide, add fadein fadeouts.
      if(this.controlsAutoHide && !this.has_external_audio){
        this.$container.onmousemove = this.mouseReset;
        this.$container.onclick = this.mouseReset;
      }
      // End Controls.
    },
    imageLoaded(idx){
      console.log("imageLoaded: " + idx);
      if(idx === 0){
        this.firstImageLoaded = true;
      }
    },
    mouseReset(){
      // One mouse move, fade in immediately.
      // Then start the clock on fading out.
      // Fade out of controls begins after controlFadeDelayMs delay.
      var fadeSecs = 0.4;
      clearTimeout(this.controlFadeTimeout);
      // Tween in the controls and control BG.
      // Take care to only have the two single tweens running at once.
      var _v = this;
      if(this.controlsTween == null){
        this.controlsTween = TweenLite.to(
            this.$controls,
            fadeSecs,
            {
              y:0,
              ease: Power2.easeInOut,
              force3D: true,
              onComplete:()=>{
                _v.controlsTween=null
              },
              callbackScope: this
          });
      }
      if(this.controlsBgTween == null){
        this.controlsBgTween = TweenLite.to(
            this.$controls_background,
            fadeSecs,
            {
              y:0,
              ease: Power2.easeInOut,
              force3D: true,
              onComplete:()=>{
                _v.controlsBgTween=null
              },
              callbackScope: this
          });
      }
      // Clocked fade out.
      clearTimeout(this.controlFadeTimeout);
      this.controlFadeTimeout = setTimeout(()=>{
        console.log("Tween controls out.");
        // Tween out the controls (down)
        TweenLite.to(this.$controls, fadeSecs, {y:100, ease: Power2.easeInOut, force3D: true});
        TweenLite.to(this.$controls_background, fadeSecs, {y:100, ease: Power2.easeInOut, force3D: true});
      }, this.controlFadeDelayMs);
    },
    preloadImage(image_index){
      // This can be WAY more intelligent, but will suffice for now.
      // Preload of an image can be trigger by either of two conditions.
      // 1) It loads `preloadImageNumber` ahead of the currentSlideIndex.
      // 2) If audio plays more than `preloadAllImagesTimeThresholdSec` seconds, it preloads all images.
      if(this.preloadAllImages){
        //console.log("preloadAllImages!")
        return true;
      }
      var load_ahead_number = this.preloadImageNumber;
      if(image_index < (this.currentSlideIndex + load_ahead_number)){
        return true;
      }else{
        return false;
      }
    },
    setDimensionValues(){
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
      if(this.$el.parentElement != null){
        // parentElement not always available.
        this.parentWidth = this.$el.parentElement.clientWidth;
        this.parentHeight = this.$el.parentElement.clientHeight;
      }
      // If the screen size is smaller than computed_width/computed_height,
      // Force fillscreen.
      if(this.parentWidth < this.defined_width || this.parentHeight < this.defined_height){
        this.forceFillscreen = true;
      }else{
        this.forceFillscreen = false;
      }

    },
    setupAudioListeners(){
      var audioEl = this.audio_ref;
      if(this.audio_source){
        audioEl.addEventListener("timeupdate", this.timeUpdate, true);
        audioEl.addEventListener("pause", this.pause, true);
        audioEl.addEventListener("play", this.play, true);
        audioEl.addEventListener("seeking", this.seeking, true);
        audioEl.addEventListener("seeked", this.seeked, true);
      }else{
        // Listeners for plyr.
        audioEl.on("timeupdate", this.timeUpdate);
        audioEl.on("pause", this.pause);
        audioEl.on("play", this.play);
        audioEl.on("seeking", this.seeking);
        audioEl.on("seeked", this.seeked);
      }
      var _v = this;
      audioEl.onloadedmetadata = function() {
        _v.audioDuration = audioEl.duration;
      };
    },
    returnImagePath(filename){
      if(filename.startsWith("blob")){
        return filename;
      }else if(this.path_to_assets){
        // Path passed into player.
        return this.path_to_assets + filename;
      }else{
        return this.path + this.imageDirectoryLargest + '/' + filename;
      }
    },
    playToggle(){
      var audioEl = this.audio_ref;
      if(audioEl.paused){
        audioEl.play()
      }else{
        audioEl.pause();
      }
    },
    handleFullscreenChange(){
      // Handles all UI and image sizing issues related to Fullscreen.
      // Triggered by onfullscreenchange listener.
      if (document.fullscreenElement) {
        this.fullscreenFillscreen = true;
      }else{
        // No fullscreen.
        this.fullscreenFillscreen = false;
      }
    },
    isFullscreen(){
      return document.fullscreenElement || document.webkitCurrentFullScreenElement;
    },
    toggleFullscreen(){
      console.log("toggleFullscreen()");
      if (this.isFullscreen()) {
        if(!document.exitFullscreen){
          // Safari.
          document.webkitExitFullscreen();
        }else{
          // Everything else.
          document.exitFullscreen();
        }

      } else {
        var $el = this.$el;
        $el.onfullscreenchange = this.handleFullscreenChange;
        if (!$el.requestFullscreen) {
          // Safari.
          $el.webkitRequestFullScreen();
        }else{
          // Everything else.
          $el.requestFullscreen();
        }
      }
    },
    toggleCaptions(){
      console.log("toggleCaptions");
      this.showImageCaptions = !this.showImageCaptions;
    },
    gotoAndPlay(sec){
      var audioEl = this.audio_ref;
      audioEl.currentTime = sec;
      audioEl.play()
      this.timeline.play(this.audio_ref.currentTime);
    },
    gotoAndPause(sec){
      var audioEl = this.audio_ref;
      audioEl.currentTime = sec;
      audioEl.pause()
      this.timeline.pause(this.audio_ref.currentTime);
    },
    nextImage(){
      var nextImageIndex = this.returnCurrentSlideIndex() + 1;
      if(nextImageIndex >= this.slideArray.length){
        // Don't go past end.
        return;
      }
      var post_transition_sec = 1; // Need to jump past the tweens.
      this.gotoAndPause((this.slideArray[nextImageIndex].timing / 1000) + post_transition_sec);
    },
    previousImage(){
      var previousImageIndex = this.returnCurrentSlideIndex() - 1;
      console.log("previousImageIndex", previousImageIndex);
      if(previousImageIndex < 0){
        // Don't go negative.
        return;
      }
      var post_transition_sec = 1; // Need to jump past the tweens.
      this.gotoAndPause((this.slideArray[previousImageIndex].timing / 1000) + post_transition_sec);
    },
    timeUpdate(){
      this.currentSlide = this.returnCurrentSlide();
      this.currentTimelineTime = this.timeline.time();
      this.currentAudioTime = this.audio_ref.currentTime;
      //this.timeline.play(this.audio_ref.currentTime);
    },
    showInitialUI(){
      if(this.showBigButton){
        TweenLite.to(this.$bigbutton, .5, {opacity:1});
        if(this.$title){
          TweenLite.to(this.$title, .5, {opacity:1});
        }

      }
    },
    setPageAttributes(){
      // Title.
      if(this.title && this.title !== 'undefined'){
        document.title = this.title;
      }
      // BG color.
      if(this.soloPlayer){
        if("body_hex" in this.metaDict){
          document.body.style.backgroundColor = "#" + this.metaDict.body_hex;
        }else{
          document.body.style.backgroundColor = "#111";
        }
      }
    },
    hideInitialUI(){
      TweenLite.to(this.$bigbutton, .5, {opacity:0});
      if(this.$title){
        TweenLite.to(this.$title, .5, {opacity:0});
      }
    },
    pause(){
      // Called from audio event listeners.
      var _currentTime = this.audio_ref.currentTime;
      if(_currentTime < 0.1){
        this.showInitialUI();
      }
      this.timeline.pause(_currentTime);
    },
    play(){
      // Called from audio event listeners.
      this.hideInitialUI();
      this.timeline.play(this.audio_ref.currentTime);
    },
    seeked(){
      console.log("seeked")
      // Check for initial UI.
      if(this.audio_ref.paused){
        var _currentTime = this.audio_ref.currentTime;
        if(_currentTime < 0.1 && this.audio_ref.paused){
          this.showInitialUI();
        }
      }
      // Update timeline.
      this.timeline.pause(this.audio_ref.currentTime);
    },
    seeking(){
      this.timeline.pause(this.audio_ref.currentTime);
    },
    checkSyncAudioAndTimeline(){
      // Work in progress, not sure if this is needed.
      // FYI, this is called from 'SoundslidesPlayer/timeline.js'.
      var timeline_time = this.timeline.time()
      var player_time = this.audio_ref.currentTime;
      var time_delta = Math.abs(timeline_time - player_time);
      //console.log("time_delta", time_delta);
      if(time_delta > this.timeDeltaAccuracySecs){
        // Correction.
        console.log("Time correction", time_delta);
        var audioEl = this.audio_ref;
        if(audioEl.paused){
          this.timeline.pause(this.audio_ref.currentTime);
        }else{
          this.timeline.play(this.audio_ref.currentTime);
        }
      }
      // Preload all check.
      if(player_time > this.preloadAllImagesTimeThresholdSec){
        this.preloadAllImages = true;
      }
    },
    renderTimeline(){
      createTimeline(this);
    },
    returnCurrentSlideIndex(){
      // This is the source of this.currentSlideIndex.
      var currentTime = this.audio_ref.currentTime;
      var currentSlideIndex = _.findLastIndex(this.slideArray, s => {
        return (s.timing / 1000) <= currentTime;
      });
      this.currentSlideIndex = currentSlideIndex;
      return currentSlideIndex;
    },
    returnCurrentSlide(){
      return this.slideArray[this.returnCurrentSlideIndex()];
    },
    returnSlideData(){
      // Send clone, not actual observable.
      return _.cloneDeep(this.slideArray);
    },
    setSlideData(_slideArray){
      this.version_number++;
      this.slideArray = _.cloneDeep(_slideArray);
      this.$nextTick(()=>{
        this.renderTimeline();
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin transform($transforms) {
  -moz-transform: $transforms;
  -o-transform: $transforms;
  -ms-transform: $transforms;
  -webkit-transform: $transforms;
  transform: $transforms;
}

.container{
  position: relative;
  background-color: black;
  overflow: hidden;
}
.player{
  position: relative;
  height: 100%;
  width: 100%
}
.controls{
  position: relative;
  z-index: 1000;
  left: 0;
  bottom: 50px;
  width: 100%;
  height: 100px;
  -webkit-transform: translate3d(0, 0, 0);
}
.controls_background{
  position: absolute;
  z-index: 999;
  background-color: none;
  background-image: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 1)) !important;
  left: 0;
  bottom: -20px;
  width: 100%;
  height: 100px;
}


.scaling{
  @media only screen and (max-width: 420px) {
    @include transform(scale(0.8, 0.8))
  }
  @media only screen and (min-width: 420px) {
    @include transform(scale(1.2, 1.2))
  }
  @media only screen and (min-width: 800px) {
    @include transform(scale(1.6, 1.6))
  }
}

.slides{
  position: absolute;
  opacity: 0;
  overflow: hidden;
}




</style>
